import { Button } from "@cleanbudget/ui/components/button";
import { IconTrash } from "@tabler/icons-react";
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { BackLink } from "@/components/back-link";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { PageHeader } from "@/components/page-header";
import { PublishedBadge } from "@/components/published-badge";
import { orpc } from "@/lib/orpc";
import { PageForm, type PageValues } from "./-components/page-form";

const pageQuery = (id: string) => orpc.page.get.queryOptions({ input: { id } });

export const Route = createFileRoute("/_admin/content/pages/$id")({
	loader: ({ context, params }) => context.queryClient.ensureQueryData(pageQuery(params.id)),
	component: EditPagePage,
});

/**
 * Edit a page. A save sends the version the editor loaded, so a page someone else saved meanwhile
 * is refused rather than overwritten; the editor then offers to load it again.
 */
function EditPagePage() {
	const { t } = useTranslation("page");
	const { id } = Route.useParams();
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const { data: page, refetch } = useSuspenseQuery(pageQuery(id));
	const [loads, setLoads] = useState(0);
	const update = useMutation({
		...orpc.page.update.mutationOptions(),
		onSuccess: (saved) => {
			queryClient.setQueryData(pageQuery(id).queryKey, saved);
			return queryClient.invalidateQueries({ queryKey: orpc.page.list.key() });
		},
	});
	const remove = useMutation({
		...orpc.page.delete.mutationOptions(),
		// The list is where this goes next: have it fresh, without the deleted page.
		onSuccess: () => queryClient.invalidateQueries({ queryKey: orpc.page.list.key(), refetchType: "all" }),
	});

	const onSubmit = async (values: PageValues) => {
		await update.mutateAsync({ id, version: page.version, ...values });
	};

	return (
		<div className="flex max-w-5xl flex-col gap-6">
			<PageHeader
				back={<BackLink to="/content/pages">{t("title")}</BackLink>}
				title={
					<span className="flex flex-wrap items-center gap-3">
						{page.title}
						<PublishedBadge published={page.published} />
					</span>
				}
			/>
			{/* A new key after a reload starts the form again from what is stored now. */}
			<PageForm
				key={loads}
				page={page}
				submitLabel={t("edit.submit")}
				onSubmit={onSubmit}
				onReload={async () => {
					await refetch();
					setLoads((count) => count + 1);
				}}
				actions={
					<ConfirmDialog
						trigger={
							<Button type="button" variant="destructive">
								<IconTrash aria-hidden />
								{t("delete.button")}
							</Button>
						}
						title={t("delete.title")}
						description={t("delete.description", { title: page.title })}
						confirmLabel={t("delete.confirm")}
						onConfirm={async () => {
							await remove.mutateAsync({ id });
							await navigate({ to: "/content/pages" });
							queryClient.removeQueries({ queryKey: pageQuery(id).queryKey });
						}}
					/>
				}
			/>
		</div>
	);
}
